import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useCameraWallFeeds } from '@repo/ui';
import { useEditorStore, useFloorplanStore } from '@repo/front-end-state';
import { findLatestDesign, isLive } from '@repo/front-end-utils';
import type { FloorPlannerCamera, RTSPEndpoint } from '@repo/frontend-types';

import Config from '../../common/Config';
import CameraPanel, { type CameraPanelStreamsStatus } from './CameraPanel';
import { useCameraPanelHost } from './useCameraPanelHost';
import type { LiveTwinConfiguration } from './useLiveTwinConfiguration';

/** No stream to read: the feeds hook is told the host owns the listing and it is empty. */
const NO_STREAMS: RTSPEndpoint[] = [];

export interface FloorPlanCameraPanelInput {
  /** The run's plan key in the store (`OnboardingPlanner`'s `sessionId`). */
  sessionId: string;
  twinId: number;
  accessToken: string | undefined;
  /** The step could not get a token: the pictures cannot be read, which is a failed read. */
  tokenRefused?: boolean;
  /** The page's read of the twin's running configuration: its streams name the picture. */
  liveConfiguration?: LiveTwinConfiguration;
  planPaneRef: { readonly current: HTMLElement | null };
}

export interface FloorPlanCameraPanel {
  /** The camera panel while exactly one live camera is selected on the floor shown, else null. */
  panel: React.ReactNode;
  /** The camera the panel shows, or null. */
  panelRefid: string | null;
  /** The panel's polite live region, always mounted so its first sentence is heard. */
  announcer: React.ReactNode;
  /** Say a sentence through that region (the plan card's grip says *back in its corner* here). */
  announce: (text: string) => void;
}

/**
 * Floor Plan's camera panel (`unified-camera-panel`, R1): Camera Feeds' panel without the stream
 * section or calibration, writing through the plan store as the planner's own camera edits do.
 *
 * - **One undo step per edit (R1.5).** The first write of an edit pushes the layout as it was; the
 *   reported write that ends it (a slider's release, a name's leave) closes it. So a drag, a key step
 *   and a typed name are each one step, as the deleted properties panel's `onEditStart` made them.
 * - **The draft saves itself.** Floor Plan's page watches the plan store and asks its debounced draft
 *   saver after any change that leaves the plan dirty (`OnboardingPage`, the Floor Plan watcher), so
 *   the adapter writes the store and nothing else.
 * - **One picture.** The feeds hook is given only the selected camera's stream, so it reads that
 *   camera's picture and nothing else; the stream list itself is the page's existing read.
 */
export const useFloorPlanCameraPanel = ({
  sessionId,
  twinId,
  accessToken,
  tokenRefused = false,
  liveConfiguration,
  planPaneRef,
}: FloorPlanCameraPanelInput): FloorPlanCameraPanel => {
  const layout = useFloorplanStore(s => s.layout);
  const storeTwinBID = useFloorplanStore(s => s.twinBID);
  const activeFloorIndex = useFloorplanStore(s => s.activeFloorIndex);
  const selectedEntityIds = useEditorStore(s => s.selectedEntityIds);
  const hasPlan = layout != null && storeTwinBID === sessionId;

  const floor = hasPlan ? layout?.floors[activeFloorIndex] : undefined;
  const design = floor ? findLatestDesign(floor.designs) : null;
  const cameras: FloorPlannerCamera[] = useMemo(
    () => (design?.cameras ?? []).filter(isLive),
    [design],
  );

  const { panelRefid, panelIsNew, panelReturnTo } = useCameraPanelHost({
    cameras,
    activeFloorIndex,
    hasPlan,
    selectedEntityIds,
    planPaneRef,
  });

  const streamsRead = liveConfiguration?.streams;
  const streams = streamsRead?.status === 'ready' ? streamsRead.value : undefined;
  // A refused token is a failed read, as on Camera Feeds: never a picture that loads for ever.
  const streamsStatus: CameraPanelStreamsStatus = tokenRefused
    ? 'failed'
    : (streamsRead?.status ?? 'loading');

  const endpointId = panelRefid
    ? (cameras.find(c => c.refid === panelRefid)?.rtspEndpointId ?? null)
    : null;
  const panelStreams = useMemo(() => {
    const own = endpointId != null ? streams?.find(s => s.id === endpointId) : undefined;
    return own ? [own] : NO_STREAMS;
  }, [streams, endpointId]);
  const { feeds } = useCameraWallFeeds({
    baseUrl: Config.middlewareApi,
    accessToken,
    twinId,
    endpoints: panelStreams,
  });

  /**
   * The edit that is open: its camera, and the layout its last write left. Its first write took the
   * undo snapshot. Any other change to the plan in between (a handle drag, an undo, another writer)
   * starts a new edit, so an edit that ended without a reported write (a slider dragged back to where
   * it started, a name typed and restored) never merges with what came after it (review).
   */
  const openEdit = useRef<{ refid: string; layout: unknown } | null>(null);
  const onWrite = useCallback(
    (refid: string, patch: Partial<FloorPlannerCamera>, report: boolean) => {
      const plan = useFloorplanStore.getState();
      const editor = useEditorStore.getState();
      const open = openEdit.current;
      if (!open || open.refid !== refid || open.layout !== plan.layout) {
        if (plan.layout) editor.pushSnapshot(plan.layout);
      }
      plan.updateCamera(refid, patch);
      editor.markEntityModified(refid);
      openEdit.current = report ? null : { refid, layout: useFloorplanStore.getState().layout };
    },
    [],
  );

  const [announcement, setAnnouncement] = useState('');
  const requestDelete = useCallback((refid: string) => {
    useEditorStore.getState().requestCameraDelete(refid);
  }, []);

  const panel = panelRefid ? (
    <CameraPanel
      feeds={feeds}
      streamsStatus={streamsStatus}
      streams={streams}
      isNew={panelIsNew}
      onWrite={onWrite}
      onAnnounce={setAnnouncement}
      streamSection={false}
      onRequestDelete={requestDelete}
      returnFocusTo={panelReturnTo}
    />
  ) : null;

  const announcer = (
    <div className="sr-only" role="status" data-testid="camera-panel-announcement">
      {announcement}
    </div>
  );

  return { panel, panelRefid, announcer, announce: setAnnouncement };
};
